import React from "react";

/**
 * Sticky action bar pinned to the bottom of the visible viewport area inside
 * an admin page. Use it to wrap the primary "Guardar" / "Save" button(s) of
 * a page so they stay reachable even when the content scrolls.
 *
 * Implementation notes:
 *  - Uses `position: sticky` so the bar sits in normal flow at the BOTTOM of
 *    its parent (the admin content container, which itself scrolls inside
 *    AdminLayout). This avoids the layout-shift problems of `position: fixed`.
 *  - Slight backdrop blur + soft border make it readable over busy forms
 *    without feeling heavy.
 *  - The component is intentionally dumb: it accepts arbitrary children so
 *    each page can render its existing button(s) without rewriting them.
 *
 *  Props:
 *    - children: the button(s) to render inside the bar.
 *    - align: "end" (default) right-aligns content; "between" spreads it.
 *    - className: optional override / extension classes.
 *    - testid: data-testid for tests.
 */
export default function StickySaveBar({
  children,
  align = "end",
  className = "",
  testid = "admin-sticky-save-bar",
}) {
  const justify = align === "between" ? "justify-between" : "justify-end";
  return (
    <div
      className={
        "sticky bottom-4 z-10 mt-4 flex flex-wrap items-center gap-2 " +
        "rounded-2xl border border-slate-200 bg-white/95 backdrop-blur " +
        "shadow-lg p-3 " + justify + " " + className
      }
      data-testid={testid}
    >
      {children}
    </div>
  );
}
